/**
 * Pure placement for a space's external wall ports.
 *
 * Placement is INTERNAL-ONLY: a pill docks on the flow wall for its direction
 * (out → flow-downstream, in → flow-upstream), centred on its owning child,
 * packed along the wall. The OUTER layout — where the parent edge happens to
 * cross the container — has no say: outer geometry is computed without any
 * knowledge of the interior, so pinning interior pills to it entangled the two
 * layers (the dropped "approach B" crossings/colocation machinery). This is
 * also what keeps placement consistent with `placeSpace`'s port-lane
 * reservation and the solver's cleared port corridors, which both assume the
 * flow walls. Kept DOM-free so it's unit-testable without React/ctx.
 */
import type {
  BoardItem,
  ExternalPort,
  PortDirection,
  FlowDirection,
} from "@archlang/scene/board";

export interface PortEntry {
  readonly item?: BoardItem;
  readonly port: ExternalPort;
}
export interface PortPlacement {
  readonly entry: PortEntry;
  /** Which wall the pill docks on. Geometry-driven: a cross-space edge docks on
   *  whichever wall it crosses. left/right = vertical walls (pills stack down);
   *  top/bottom = horizontal walls (pills stack across). */
  readonly side: "left" | "right" | "top" | "bottom";
  /** Pill centre (space coords). */
  readonly px: number;
  readonly py: number;
  /** Connector origin on the owning item. */
  readonly ix: number;
  readonly iy: number;
}
export interface PortGeom {
  readonly spaceW: number;
  readonly spaceH: number;
  readonly originX: number;
  readonly originY: number;
}

// Pill geometry. MUST stay in lockstep with `PORT_LANE` in
// `@archlang/scene/board` portGeom.ts — that's the wall margin `placeSpace`
// reserves for these pills; if these grow past it, the pills overflow the lane.
export const PORT_W = 150;
export const PORT_H = 28;
// MIRRORS `PORT_WALL_GAP` in @archlang/scene/board portGeom.ts — the pill's
// outer breather to the carpet edge. Keep in lockstep (see the note above).
const WALL_GAP = 24;
const PORT_GAP = 8;

export interface PortConnector {
  /** Cubic path from the source end to the target end. */
  readonly d: string;
  /** Direction the path travels AT its arrow end (endpoint − last control). The
   *  arrowhead (marker-end, orient=auto) points this way; a sign flip here is
   *  the "arrow points back at the node" bug. */
  readonly endDx: number;
  readonly endDy: number;
}

/**
 * The bezier joining an item's wall to its port pill.
 *
 * The pill is a fixed `PORT_W` wide and hugs the wall, so its INNER edge (the
 * face turned toward the space) sits at `px ∓ PORT_W/2`; the connector meets it
 * there. Both control handles are anchored to their OWN endpoint — the pill
 * handle to the pill edge, NOT the pill centre `px` — so the end tangent always
 * runs into the pill. Anchoring the handle to `px` reverses the tangent (arrow
 * points back at the node) whenever `dxC < PORT_W/2`.
 *
 *   "out" port → item sends → arrow points from item toward the pill.
 *   "in"  port → item receives → arrow points from the pill toward the item.
 *
 * An "inout" port draws the "out" geometry; the caller adds a `marker-start`
 * so the single stroke carries a head at each end. Two overlaid paths would
 * double the stroke weight and read as a thicker line, not a mutual one.
 */
export function portConnectorPath(
  side: "left" | "right" | "top" | "bottom",
  direction: PortDirection,
  ix: number,
  iy: number,
  px: number,
  py: number,
): PortConnector {
  if (side === "top" || side === "bottom") {
    // HORIZONTAL wall: the pill's inner face is its BOTTOM edge (top wall) or TOP
    // edge (bottom wall); the connector runs VERTICALLY (roles of x and y swap).
    const dyC = Math.max(60, Math.abs(py - iy) * 0.4);
    const ey = side === "bottom" ? py - PORT_H / 2 : py + PORT_H / 2;
    const cPill = side === "bottom" ? ey - dyC : ey + dyC;
    const cItem = side === "bottom" ? iy + dyC : iy - dyC;
    if (direction !== "in") {
      // item (ix,iy) → pill (px,ey); arrow ends at the pill.
      return { d: `M ${ix} ${iy} C ${ix} ${cItem}, ${px} ${cPill}, ${px} ${ey}`, endDx: 0, endDy: ey - cPill };
    }
    // pill (px,ey) → item (ix,iy); arrow ends at the item.
    return { d: `M ${px} ${ey} C ${px} ${cPill}, ${ix} ${cItem}, ${ix} ${iy}`, endDx: 0, endDy: iy - cItem };
  }
  const dxC = Math.max(60, Math.abs(px - ix) * 0.4);
  const ex = side === "right" ? px - PORT_W / 2 : px + PORT_W / 2;
  const cPill = side === "right" ? ex - dxC : ex + dxC;
  const cItem = side === "right" ? ix + dxC : ix - dxC;
  if (direction !== "in") {
    // item (ix,iy) → pill (ex,py); arrow ends at the pill.
    return {
      d: `M ${ix} ${iy} C ${cItem} ${iy}, ${cPill} ${py}, ${ex} ${py}`,
      endDx: ex - cPill,
      endDy: py - py,
    };
  }
  // pill (ex,py) → item (ix,iy); arrow ends at the item.
  return {
    d: `M ${ex} ${py} C ${cPill} ${py}, ${cItem} ${iy}, ${ix} ${iy}`,
    endDx: ix - cItem,
    endDy: iy - iy,
  };
}

/** CSS box (absolute left/top, + a hug-the-wall transform on the far side) that
 *  centres a `PORT_W × PORT_H` pill on (px,py) against its wall. Pure so the
 *  4-wall geometry is unit-testable without rendering the component. */
export function portPillBox(
  side: "left" | "right" | "top" | "bottom",
  px: number,
  py: number,
): { left: number; top: number; transform?: string } {
  const left = side === "right" ? px + PORT_W / 2 : px - PORT_W / 2;
  const top = side === "bottom" ? py + PORT_H / 2 : py - PORT_H / 2;
  const transform = side === "right" ? "translateX(-100%)" : side === "bottom" ? "translateY(-100%)" : undefined;
  return transform ? { left, top, transform } : { left, top };
}

/** The pill's direction glyph. Reflects the call DIRECTION, not the wall: an IN
 *  call points INTO the space (away from the wall it hugs), an OUT call points
 *  toward the wall/peer; horizontal walls use the vertical axis (↑/↓). A peer
 *  called both ways is one port and reads ⇄ / ⇅ along the wall's own axis. */
export function portGlyph(
  side: "left" | "right" | "top" | "bottom",
  direction: PortDirection,
): string {
  const vertical = side === "top" || side === "bottom";
  if (direction === "inout") return vertical ? "⇅" : "⇄";
  if (direction === "in") return side === "top" ? "↓" : side === "bottom" ? "↑" : side === "right" ? "←" : "→";
  return side === "top" ? "↑" : side === "bottom" ? "↓" : side === "right" ? "→" : "←";
}

export function resolvePortPlacements(
  items: readonly BoardItem[],
  geom: PortGeom,
  direction: FlowDirection = "LR",
): PortPlacement[] {
  const { spaceW, spaceH, originX, originY } = geom;
  const all: PortEntry[] = [];
  for (const item of items) for (const port of item.externalPorts ?? []) all.push({ item, port });
  if (all.length === 0) return [];

  const orderKey = (e: PortEntry) => e.item!.y * 10000 + e.port.peerLabel.charCodeAt(0);
  const left: PortEntry[] = [];
  const right: PortEntry[] = [];
  const top: PortEntry[] = [];
  const bottom: PortEntry[] = [];
  // Dock on the FLOW wall — an out-call exits the flow-DOWNSTREAM wall, an
  // in-call enters from the flow-UPSTREAM wall. This mirrors the solver's
  // flow-space corridor axis so pill lane ≡ cleared lane in every direction.
  // For LR it is out→right / in→left — the reading convention.
  //
  // A mutual peer has no upstream/downstream answer, so it goes downstream —
  // the wall the conversation's outbound half would have used. Whatever this
  // picks, `clearPortCorridors` and the corridor metric must pick the same,
  // or the reserved lane is not the lane the pill lands in.
  const downstream = direction === "RL" ? left : direction === "TB" ? bottom : direction === "BT" ? top : right;
  const upstream = direction === "RL" ? right : direction === "TB" ? top : direction === "BT" ? bottom : left;
  for (const e of [...all].sort((a, b) => orderKey(a) - orderKey(b))) {
    (e.port.direction === "in" ? upstream : downstream).push(e);
  }

  const out: PortPlacement[] = [];
  // The connector docks on the callee at the pill's own coordinate — the stub
  // runs straight to its pill instead of kinking from the face centre. Clamp to
  // the node body (along the given axis) so a packed-away pill still lands the
  // connector on its owner's face.
  const bodyAnchor = (lo: number, len: number, v: number) => {
    const inset = Math.min(10, len / 2);
    return Math.max(lo + inset, Math.min(lo + len - inset, v));
  };
  // Forward-only de-overlap at `pitch`, then keep the whole run on the track
  // [lo,hi]: longer than the track → distribute evenly (ordered, on-screen,
  // gracefully compressed); else shift to fit. Gated to runs ≥2 — a lone pill
  // sits at its owner's centre, always inside the track. One axis-agnostic
  // packer for all walls.
  const pack = (
    arr: PortPlacement[],
    get: (p: PortPlacement) => number,
    set: (p: PortPlacement, v: number) => void,
    lo: number, hi: number, pitch: number, half: number,
  ) => {
    arr.sort((a, b) => get(a) - get(b));
    for (let i = 1; i < arr.length; i++) { const m = get(arr[i - 1]!) + pitch; if (get(arr[i]!) < m) set(arr[i]!, m); }
    if (arr.length >= 2) {
      const start = lo + half, end = hi - half;
      const first = get(arr[0]!), last = get(arr[arr.length - 1]!);
      if (last - first > end - start) { const step = (end - start) / (arr.length - 1); arr.forEach((p, i) => set(p, start + i * step)); }
      else { const shift = first < start ? start - first : last > end ? end - last : 0; if (shift !== 0) for (const p of arr) set(p, get(p) + shift); }
    }
  };
  // Place each owner's ports as a centred run along the wall axis.
  const groupRun = (list: PortEntry[], centre: (it: BoardItem) => number, span: number, put: (e: PortEntry, coord: number) => void) => {
    const grouped = new Map<string, PortEntry[]>();
    for (const e of list) {
      const arr = grouped.get(e.item!.id);
      if (arr) arr.push(e);
      else grouped.set(e.item!.id, [e]);
    }
    for (const [, group] of grouped) {
      const item = group[0]!.item!;
      const total = group.length;
      const start = centre(item) - (total * span + (total - 1) * PORT_GAP) / 2;
      group.forEach((e, i) => put(e, start + i * (span + PORT_GAP) + span / 2));
    }
  };

  // VERTICAL walls (left/right): pill fixed-x at the wall, py varies down the
  // height; connectors run horizontally. (Behaviour preserved from the original.)
  const layOutV = (list: PortEntry[], side: "left" | "right") => {
    const px = side === "right" ? originX + spaceW - WALL_GAP - PORT_W / 2 : originX + WALL_GAP + PORT_W / 2;
    const local: PortPlacement[] = [];
    const put = (e: PortEntry, py: number) =>
      local.push({ entry: e, side, px, py, ix: side === "right" ? e.item!.x + e.item!.w : e.item!.x, iy: bodyAnchor(e.item!.y, e.item!.h, py) });
    groupRun(list, (it) => it.y + it.h / 2, PORT_H, put);
    pack(local, (p) => p.py, (p, v) => ((p as { py: number }).py = v), originY, originY + spaceH, PORT_H + PORT_GAP, PORT_H / 2);
    for (const p of local) (p as { iy: number }).iy = bodyAnchor(p.entry.item!.y, p.entry.item!.h, p.py);
    for (const p of local) out.push(p);
  };
  // HORIZONTAL walls (top/bottom): pill fixed-y at the wall, px varies across the
  // width; connectors run vertically. Transpose of layOutV.
  const layOutH = (list: PortEntry[], side: "top" | "bottom") => {
    const py = side === "bottom" ? originY + spaceH - WALL_GAP - PORT_H / 2 : originY + WALL_GAP + PORT_H / 2;
    const local: PortPlacement[] = [];
    const put = (e: PortEntry, px: number) =>
      local.push({ entry: e, side, px, py, ix: bodyAnchor(e.item!.x, e.item!.w, px), iy: side === "bottom" ? e.item!.y + e.item!.h : e.item!.y });
    groupRun(list, (it) => it.x + it.w / 2, PORT_W, put);
    pack(local, (p) => p.px, (p, v) => ((p as { px: number }).px = v), originX, originX + spaceW, PORT_W + PORT_GAP, PORT_W / 2);
    for (const p of local) (p as { ix: number }).ix = bodyAnchor(p.entry.item!.x, p.entry.item!.w, p.px);
    for (const p of local) out.push(p);
  };
  layOutV(left, "left");
  layOutV(right, "right");
  layOutH(top, "top");
  layOutH(bottom, "bottom");
  return out;
}
